// src/components/SystemStatus.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Divider,
  LinearProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import SpeedIcon from "@mui/icons-material/Speed";
import TimelineIcon from "@mui/icons-material/Timeline";
import LanIcon from "@mui/icons-material/Lan";
import CloudIcon from "@mui/icons-material/Cloud";
import ApiIcon from "@mui/icons-material/Api";
import BoltIcon from "@mui/icons-material/Bolt";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ShieldIcon from "@mui/icons-material/Shield";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import SyncAltIcon from "@mui/icons-material/SyncAlt";

// ✅ SEO hooks
import { useSEO, useJsonLD } from "./SEO"; // عدّل المسار إذا لزم

const BG = "#263238";
const SURFACE = "rgba(38,50,56,0.85)";
const BORDER = "rgba(255,255,255,0.08)";
const TEXT = "#EAEFF2";
const MUTED = "#B0BEC5";
const ACCENT = "#F0B90B";

const LS_KEY = "systemStatus_cache_v1";
const REFRESH_MS = 24 * 60 * 60 * 1000;

const legend = [
  { label: "Operational", color: "#48C774", icon: <CheckCircleIcon /> },
  { label: "Degraded", color: "#FFB020", icon: <ErrorOutlineIcon /> },
  { label: "Maintenance", color: "#42A5F5", icon: <PendingActionsIcon /> },
];

function StatusPill({ state = "operational" }) {
  const map = {
    operational: {
      color: "#48C774",
      icon: <CheckCircleIcon />,
      text: "Operational",
    },
    degraded: {
      color: "#FFB020",
      icon: <ErrorOutlineIcon />,
      text: "Degraded",
    },
    maintenance: {
      color: "#42A5F5",
      icon: <PendingActionsIcon />,
      text: "Maintenance",
    },
  };
  const s = map[state] ?? map.operational;
  return (
    <Chip
      size="small"
      icon={s.icon}
      label={s.text}
      sx={{
        bgcolor: `${s.color}22`,
        color: s.color,
        border: `1px solid ${s.color}55`,
        fontWeight: 700,
      }}
    />
  );
}

// ---- Random generators (as-is)
function randRange(min, max, decimals = 0) {
  const n = Math.random() * (max - min) + min;
  return parseFloat(n.toFixed(decimals));
}
function weightedPick(entries) {
  const sum = entries.reduce((a, e) => a + e.w, 0);
  let r = Math.random() * sum;
  for (const e of entries) {
    if ((r -= e.w) <= 0) return e.v;
  }
  return entries[0].v;
}

function generateStatusBundle() {
  const now = Date.now();

  const uptime = randRange(99.9, 99.99, 2);
  const apiLatencyMs = randRange(28, 55, 0);
  const wsUptime = randRange(99.88, 99.98, 2);

  const services = [
    {
      icon: <CandlestickChartIcon />,
      name: "Trading Engine",
      desc: "Core matching & risk checks",
    },
    {
      icon: <ShowChartIcon />,
      name: "Spot Markets",
      desc: "Order book, tickers, trades feed",
    },
    {
      icon: <BoltIcon />,
      name: "Futures (Perpetuals)",
      desc: "Leverage, funding, liquidation",
    },
    {
      icon: <SyncAltIcon />,
      name: "AI Trade",
      desc: "Signals & automation hooks",
    },
    {
      icon: <CloudIcon />,
      name: "Price Feeds",
      desc: "Aggregated market data",
    },
    { icon: <ApiIcon />, name: "Public API", desc: "REST & WebSocket" },
    { icon: <LanIcon />, name: "Deposits", desc: "Multi-network settlement" },
    { icon: <LanIcon />, name: "Withdrawals", desc: "Queued & allow-listed" },
    {
      icon: <ShieldIcon />,
      name: "KYC / Compliance",
      desc: "Verification & reviews",
    },
    {
      icon: <SupportAgentIcon />,
      name: "Live Support",
      desc: "24/7 chat & tickets",
    },
  ].map((s) => ({
    ...s,
    state: weightedPick([
      { v: "operational", w: 90 },
      { v: "degraded", w: 8 },
      { v: "maintenance", w: 2 },
    ]),
  }));

  const regions = [
    { region: "EU" },
    { region: "MENA" },
    { region: "APAC" },
    { region: "US (restricted)" },
  ].map((r) => ({
    ...r,
    state: weightedPick([
      { v: "operational", w: r.region === "US (restricted)" ? 70 : 92 },
      { v: "degraded", w: r.region === "US (restricted)" ? 25 : 7 },
      { v: "maintenance", w: 3 },
    ]),
  }));

  const incidents = [
    {
      title: "Minor latency on Public API",
      status: "Resolved",
      time: "Today, 11:20–11:37",
      impact:
        "Elevated response time (~200–280ms) on /markets due to cache warm-up.",
      actions: "Expanded cache window & adaptive backoff.",
    },
    {
      title: "Scheduled maintenance — Futures",
      status: "Completed",
      time: "Yesterday, 02:00–02:12",
      impact: "Short read-only window; trading engine remained available.",
      actions: "Schema migration & funding calculator patch.",
    },
  ];

  return {
    createdAt: now,
    uptime,
    apiLatencyMs,
    wsUptime,
    services,
    regions,
    incidents,
  };
}

function loadCache() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
function saveCache(bundle) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(bundle));
  } catch {}
}

export default function SystemStatus() {
  // ✅ SEO meta tags
  useSEO({
    title: "System Status — 1CryptoX | Uptime, API Latency, WebSocket Health",
    description:
      "Live health dashboard for 1CryptoX: uptime, API latency, WebSocket stability, service regions, and recent incidents/maintenance windows.",
    keywords: [
      "1cryptox status",
      "crypto exchange status",
      "uptime",
      "api latency",
      "websocket status",
      "incidents",
      "maintenance",
    ],
    canonical: "https://www.1cryptox.com/status",
    og: {
      title: "1CryptoX System Status",
      description:
        "Check live uptime, API latency, WebSocket stability, and incident history.",
      url: "https://www.1cryptox.com/status",
      image: "https://www.1cryptox.com/og/status-card.jpg", // بدّل لاحقًا
      siteName: "1CryptoX",
      type: "website",
    },
    twitter: {
      card: "summary_large_image",
      title: "1CryptoX System Status",
      description:
        "Live health of 1CryptoX services with uptime and incident history.",
      image: "https://www.1cryptox.com/og/status-card.jpg", // بدّل لاحقًا
    },
  });

  // ✅ JSON-LD (FAQPage) — أسئلة شائعة لصفحة الحالة
  useJsonLD("status-faq", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How often is the status page updated?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Metrics refresh automatically; incident log updates are posted as soon as events are verified.",
        },
      },
      {
        "@type": "Question",
        name: "What if I see degraded performance?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Check the region/service blocks for localized impact. You can subscribe to updates or contact 24/7 support.",
        },
      },
      {
        "@type": "Question",
        name: "Do scheduled maintenances halt trading?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "Most maintenances are rolling or read-only windows. Core trading remains available unless explicitly stated.",
        },
      },
    ],
  });

  // الكاش والتحديث كل 24 ساعة (محلي لكل متصفح)
  const [data, setData] = useState(() => {
    const cached = loadCache();
    const now = Date.now();
    if (cached && now - cached.createdAt < REFRESH_MS) return cached;
    const fresh = generateStatusBundle();
    saveCache(fresh);
    return fresh;
  });

  const timerRef = useRef(null);
  useEffect(() => {
    const now = Date.now();
    const nextAt = data.createdAt + REFRESH_MS;
    const delay = Math.max(1000, nextAt - now);
    timerRef.current && clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const fresh = generateStatusBundle();
      saveCache(fresh);
      setData(fresh);
    }, delay);
    return () => {
      timerRef.current && clearTimeout(timerRef.current);
    };
  }, [data.createdAt]);

  const lastUpdated = useMemo(() => new Date(data.createdAt).toLocaleString(), [
    data.createdAt,
  ]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        px: { xs: 2, md: 4 },
        py: { xs: 4, md: 6 },
        position: "relative",
        overflow: "hidden",
        background:
          "radial-gradient(60% 60% at 70% 30%, rgba(240,185,11,0.10) 0%, rgba(240,185,11,0.03) 100%)",
        bgcolor: "#0B0E11",
      }}
    >
      <Container maxWidth="lg" disableGutters>
        {/* Header + Legend + KPIs */}
        <Card
          sx={{
            bgcolor: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
            backdropFilter: "blur(8px)",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 900, color: TEXT }}>
                  System Status
                </Typography>
                <Typography sx={{ color: MUTED }}>
                  Realtime health. Last updated: {lastUpdated}
                </Typography>
              </Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                {legend.map((l) => (
                  <Chip
                    key={l.label}
                    icon={l.icon}
                    label={l.label}
                    sx={{
                      bgcolor: `${l.color}22`,
                      color: l.color,
                      border: `1px solid ${l.color}55`,
                      fontWeight: 700,
                    }}
                  />
                ))}
              </Stack>
            </Stack>

            <Divider sx={{ my: 2, borderColor: BORDER }} />

            {/* KPIs */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
                  <CardContent>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <SpeedIcon sx={{ color: ACCENT }} />
                      <Box>
                        <Typography sx={{ color: MUTED, fontSize: 13 }}>
                          API Latency
                        </Typography>
                        <Typography
                          sx={{ color: TEXT, fontWeight: 900, fontSize: 18 }}
                        >
                          {data.apiLatencyMs} ms
                        </Typography>
                      </Box>
                    </Stack>
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.max(
                          0,
                          Math.min(100, (200 - data.apiLatencyMs) / 2)
                        )}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
                  <CardContent>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <TimelineIcon sx={{ color: ACCENT }} />
                      <Box>
                        <Typography sx={{ color: MUTED, fontSize: 13 }}>
                          Uptime (30d)
                        </Typography>
                        <Typography
                          sx={{ color: TEXT, fontWeight: 900, fontSize: 18 }}
                        >
                          {data.uptime}%
                        </Typography>
                      </Box>
                    </Stack>
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={data.uptime}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
                  <CardContent>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <CloudIcon sx={{ color: ACCENT }} />
                      <Box>
                        <Typography sx={{ color: MUTED, fontSize: 13 }}>
                          WebSocket Uptime (7d)
                        </Typography>
                        <Typography
                          sx={{ color: TEXT, fontWeight: 900, fontSize: 18 }}
                        >
                          {data.wsUptime}%
                        </Typography>
                      </Box>
                    </Stack>
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={data.wsUptime}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Services */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {data.services.map((s) => (
            <Grid item key={s.name} xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: "100%",
                  bgcolor: BG,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "999px",
                        bgcolor: "rgba(255,255,255,0.06)",
                        border: `1px solid ${BORDER}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: ACCENT,
                        "& svg": { fontSize: 22 },
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Typography sx={{ color: TEXT, fontWeight: 800 }}>
                          {s.name}
                        </Typography>
                        <StatusPill state={s.state} />
                      </Stack>
                      <Typography sx={{ color: MUTED, mt: 0.5 }}>
                        {s.desc}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Regions + Incidents */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ color: TEXT, fontWeight: 900, mb: 1 }}
                >
                  Regions
                </Typography>
                <Stack spacing={1}>
                  {data.regions.map((r) => (
                    <Stack
                      key={r.region}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography sx={{ color: TEXT }}>{r.region}</Typography>
                      <StatusPill state={r.state} />
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ bgcolor: BG, border: `1px solid ${BORDER}` }}>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ color: TEXT, fontWeight: 900, mb: 1 }}
                >
                  Incidents & Maintenance
                </Typography>
                {data.incidents.map((i, idx) => (
                  <Accordion
                    key={idx}
                    disableGutters
                    sx={{
                      bgcolor: "transparent",
                      borderTop: idx === 0 ? `1px solid ${BORDER}` : "none",
                      borderBottom: `1px solid ${BORDER}`,
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: MUTED }} />}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        sx={{ width: "100%", justifyContent: "space-between" }}
                      >
                        <Typography sx={{ color: TEXT, fontWeight: 700 }}>
                          {i.title}
                        </Typography>
                        <Chip
                          size="small"
                          label={i.status}
                          sx={{
                            bgcolor: "rgba(240,185,11,0.12)",
                            color: ACCENT,
                            border: `1px solid rgba(240,185,11,0.25)`,
                            fontWeight: 700,
                          }}
                        />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography sx={{ color: MUTED, mb: 0.5 }}>
                        <b>Window:</b> {i.time}
                      </Typography>
                      <Typography sx={{ color: MUTED, mb: 0.5 }}>
                        <b>Impact:</b> {i.impact}
                      </Typography>
                      <Typography sx={{ color: MUTED }}>
                        <b>Actions:</b> {i.actions}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}

                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Tooltip title="Refresh now (override cache)">
                    <Button
                      variant="outlined"
                      sx={{ color: TEXT, borderColor: BORDER }}
                      onClick={() => {
                        const fresh = generateStatusBundle();
                        saveCache(fresh);
                        setData(fresh);
                      }}
                    >
                      Refresh Now
                    </Button>
                  </Tooltip>
                  <Button
                    href="/help-center"
                    sx={{ color: ACCENT, fontWeight: 800 }}
                  >
                    Visit Help Center
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
